import { useT } from '@/infra/i18n';
import { COLORS, SPACING, RADIUS, FONT } from '@/presentation/theme/tokens';
import { overlayStyle, cardStyle } from '@/presentation/modals/modal-shared';
import { expeditionBriefing } from '@/domain/world/expedition-briefing';
import type { BiomeId } from '@/domain/world/types';
import type { AlchemyType } from '@/domain/genetics/types';
import { CombatIcon } from '@/presentation/icons/CombatIcon';

interface Props {
  open: boolean;
  onCancel: () => void;
  onProceed: () => void;
  biome: BiomeId;
  creatureTypes: AlchemyType[];
}

export function EnterRunModal({ open, onCancel, onProceed, biome, creatureTypes }: Props) {
  const t = useT();
  if (!open) return null;
  const briefing = expeditionBriefing(biome, creatureTypes);
  return (
    <div role="dialog" data-testid="enter-run-modal" style={overlayStyle} onClick={onCancel}>
      <div style={cardStyle} onClick={(e) => e.stopPropagation()}>
        <h2 style={titleStyle}><CombatIcon id="expedition" size={28} label="" /> {t('expedition.confirm.title')}</h2>
        <p style={bodyStyle}>{t('expedition.confirm.body')}</p>
        <div style={briefingBox}>
          <strong>{t(`biome.${biome}`)}</strong>
          <span data-testid="expedition-risk">{t('expedition.briefing.risk')}: {t(`expedition.briefing.risk.${briefing.risk}`)}</span>
          <span data-testid="expedition-enemies">{t('expedition.briefing.enemies')}: {briefing.enemyTypes.map((type) => t(`type.${type}`)).join(' · ')}</span>
          <span data-testid="expedition-resources">{t('expedition.briefing.resources')}: {briefing.resources.map((item) => t(`item.${item}`)).join(' · ')}</span>
        </div>
        <p style={warnStyle}><CombatIcon id="affinity-hostile" size={18} label="" /> {t('expedition.confirm.warning')}</p>
        <div style={btnRow}>
          <button data-testid="enter-cancel" onClick={onCancel} style={cancelBtn}>
            {t('expedition.confirm.cancel')}
          </button>
          <button data-testid="enter-proceed" onClick={onProceed} style={proceedBtn}>
            {t('expedition.confirm.proceed')}
          </button>
        </div>
      </div>
    </div>
  );
}

const titleStyle: React.CSSProperties = {
  margin: `0 0 ${SPACING.md}px 0`,
  fontSize: FONT.sizeLg,
  color: COLORS.danger,
  textAlign: 'center',
};
const bodyStyle: React.CSSProperties = {
  color: COLORS.text,
  fontSize: FONT.sizeSm,
  marginBottom: SPACING.sm,
};
const warnStyle: React.CSSProperties = {
  color: COLORS.warn,
  fontSize: FONT.sizeXs,
  background: 'rgba(255,204,102,0.1)',
  padding: SPACING.sm,
  borderRadius: RADIUS.sm,
  marginBottom: SPACING.md,
};
const briefingBox: React.CSSProperties = {
  display: 'grid', gap: 5, margin: `${SPACING.sm}px 0`, padding: SPACING.sm,
  background: COLORS.surfaceAlt, borderLeft: `4px solid ${COLORS.accent}`,
  color: COLORS.text, fontSize: FONT.sizeXs,
};
const btnRow: React.CSSProperties = {
  display: 'flex',
  gap: SPACING.sm,
};
const cancelBtn: React.CSSProperties = {
  flex: 1,
  background: 'transparent',
  color: COLORS.textDim,
  border: `1px solid ${COLORS.border}`,
  borderRadius: RADIUS.md,
  padding: '8px 14px',
  cursor: 'pointer',
  fontSize: FONT.sizeSm,
};
const proceedBtn: React.CSSProperties = {
  flex: 1,
  background: COLORS.danger,
  color: '#fff',
  border: `1px solid ${COLORS.danger}`,
  borderRadius: RADIUS.md,
  padding: '8px 14px',
  cursor: 'pointer',
  fontWeight: 600,
  fontSize: FONT.sizeSm,
};
